import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const AgencyAccordion = ({
  children,
  defaultExpanded = true,
  label = "Section Title",
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [height, setHeight] = useState(defaultExpanded ? "auto" : "0px");
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const contentHeight = contentRef.current.scrollHeight;

    if (isExpanded) {
      setHeight(contentHeight + "px");
      // After transition, allow height to adjust naturally
      const timeout = setTimeout(() => {
        setHeight("auto");
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      // When collapsing from "auto", force an explicit height first then transition to 0
      if (height === "auto") {
        setHeight(contentHeight + "px");
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setHeight("0px");
          });
        });
      } else {
        setHeight("0px");
      }
    }
  }, [isExpanded, children, height]);

  return (
    <div className={`w-full ${className} mb-10`}>
      {/* Accordion Header */}
      <div
        className="flex items-center cursor-pointer"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setIsExpanded((prev) => !prev);
          }
        }}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className="flex-grow border-b-2 border-gray-600 flex items-center text-lg">
          <span className="text-black font-semibold">{label}</span>
          <span
            className={`text-gray-600 font-normal transition-opacity duration-300 ease-in-out ${
              isExpanded ? "opacity-0" : "opacity-100"
            }`}
          >
            <KeyboardArrowRightIcon />
          </span>
        </div>
      </div>
      {/* Accordion Content */}
      <div
        ref={contentRef}
        style={{
          transition: "height 300ms ease-in-out",
          height: height,
          overflow: "hidden",
        }}
      >
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
};

AgencyAccordion.propTypes = {
  children: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  label: PropTypes.string,
  className: PropTypes.string,
};

export default AgencyAccordion;